import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { canonicalRoundDisplayName } from "@sportex100/shared/utils/competitionIdentity.ts";

export type ScoringRule = {
  round_code: string;
  round_name: string;
  round_sequence: number;
  game_win_points: number;
  round_win_points: number;
};

export type ScoringRulesData = {
  competition_code: string;
  season_code: string;
  qualification_points?: number;
  series_win_required?: boolean;
  rules: ScoringRule[];
};

export type UseScoringRulesResult = {
  rules: ScoringRule[];
  pointsPerRound: Array<{ gameWin: number; roundBonus: number; seriesWinRequired?: boolean }>;
  qualificationPoints: number;
  loading: boolean;
  error: string | null;
};

export function useScoringRules(competitionCode: string, seasonCode: string): UseScoringRulesResult {
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const canLoad = Boolean(isAuthenticated && competitionCode && seasonCode);
  const queryKey = useMemo(
    () => ["scoring-rules", competitionCode, seasonCode],
    [competitionCode, seasonCode],
  );

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const token = await acquireToken();
      if (!token) {
        throw new Error("Failed to acquire authentication token");
      }

      const response = await fetch(
        apiUrl(`/api/competitions/${competitionCode}/seasons/${seasonCode}/scoring-rules`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch scoring rules: ${response.statusText}`);
      }

      const data: ScoringRulesData = await response.json();
      return {
        qualificationPoints: data.qualification_points ?? 0,
        seriesWinRequired: data.series_win_required ?? false,
        rules: data.rules.map((rule) => ({
          ...rule,
          round_name: canonicalRoundDisplayName(rule.round_code, rule.round_name) ?? rule.round_name,
        })),
      };
    },
  });

  const rules = canLoad ? query.data?.rules ?? [] : [];
  const pointsPerRound = useMemo(() => {
    const sortedRules = [...rules].sort((a, b) => a.round_sequence - b.round_sequence);
    return sortedRules.map((rule) => ({
      gameWin: rule.game_win_points,
      roundBonus: rule.round_win_points,
      seriesWinRequired: query.data?.seriesWinRequired ?? false,
    }));
  }, [rules, query.data?.seriesWinRequired]);

  return {
    rules,
    pointsPerRound,
    qualificationPoints: query.data?.qualificationPoints ?? 0,
    loading: canLoad ? query.isLoading : false,
    error: canLoad && query.error ? (query.error as Error).message : null,
  };
}
